<script lang="ts">
  import { untrack } from 'svelte';
  import type { Project, ProjectStatus } from '../../lib/types';
  import { FOCUS_ACCENTS, FOCUS_STATUSES } from '../../lib/projectStatusActions';
  import SegmentedControl from './SegmentedControl.svelte';
  import ChipRow, { type Chip } from './ChipRow.svelte';
  import MobileFocusCard from './MobileFocusCard.svelte';

  interface Props {
    /** Both branches use the parent's same lane derivation, including almost → Active. */
    getProjects: (status: ProjectStatus) => Project[];
    periods: Chip[];
    onAddProject: (status: ProjectStatus) => void;
  }
  let { getProjects, periods, onAddProject }: Props = $props();

  function initialStatus(): ProjectStatus {
    try {
      const saved = sessionStorage.getItem('focus:status');
      if (FOCUS_STATUSES.some(({ status }) => status === saved)) return saved as ProjectStatus;
    } catch {
      // Storage can be blocked. The default still works.
    }
    return getProjects('active').length ? 'active' : 'planned';
  }
  // Choose once on mount: moving the last project must not change the selected tab.
  let selected = $state<ProjectStatus>(untrack(initialStatus));
  let options = $derived(
    FOCUS_STATUSES.map(({ status, label }) => ({
      value: status,
      label,
      icon: status === 'planned' ? '📋' : status === 'active' ? '🔄' : '✔️',
      count: getProjects(status).length,
      accent: FOCUS_ACCENTS[status as keyof typeof FOCUS_ACCENTS],
    })),
  );
  let projects = $derived(getProjects(selected));
  function select(status: ProjectStatus) {
    selected = status;
    try {
      sessionStorage.setItem('focus:status', status);
    } catch {
      /* optional persistence */
    }
  }
</script>

<div class="mobile-focus">
  {#if periods.length}<ChipRow chips={periods} label="Focus periods" revealActive />{/if}
  <div class="status-tabs">
    <SegmentedControl {options} value={selected} label="Project status" onChange={select} />
  </div>
  <section class="cards" aria-label="{selected} projects">
    {#each projects as project (project.id)}
      <MobileFocusCard {project} />
    {:else}
      <div class="empty">
        <p>No {selected} projects</p>
        <button type="button" onclick={() => onAddProject(selected)}>+ Add project</button>
      </div>
    {/each}
    {#if projects.length}
      <button type="button" class="add-project" onclick={() => onAddProject(selected)}
        >+ Add project</button
      >
    {/if}
  </section>
</div>

<style>
  .mobile-focus {
    min-width: 0;
    height: 100%;
    overflow-y: auto;
    background: #f8fafc;
  }
  .status-tabs {
    padding: 8px 12px;
  }
  .cards {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 12px 16px;
  }
  .empty {
    padding: 32px 12px;
    text-align: center;
    color: #64748b;
    font-size: 14px;
  }
  button {
    min-height: 44px;
    padding: 0 16px;
    border: 1px solid #bfdbfe;
    border-radius: 10px;
    background: #eff6ff;
    color: #2563eb;
    font: inherit;
    font-size: 14px;
    cursor: pointer;
  }
</style>
